import { useI18n, translate as t } from "../i18n";
import { useEffect, useRef, useState } from "react";
import emailjs, { EmailJSResponseStatus } from "@emailjs/browser";
import { motion } from "motion/react";
import { Check, ChevronDown } from "lucide-react";
import ReCAPTCHA from "react-google-recaptcha";
import { EMPTY_CONTACT_DRAFT, useContactChat, type ContactFormDraft } from "../context/ContactChatContext";
import { CONTACT_CC_EMAIL, PUBLISHING_EMAIL } from "../data/contactInfo";
import PillButton from "./PillButton";
import { SectionArrowRight } from "./SectionArrowDown";
import type { PillButtonVariant } from "./PillButton";

interface FormErrors {
  name?: string;
  companyName?: string;
  companyWebsite?: string;
  companySize?: string;
  email?: string;
  interest?: string;
  message?: string;
  captcha?: string;
}

const MINIMUM_FORM_FILL_TIME_MS = 2_000;
const INTEREST_OPTIONS = [
  "Art development",
  "Game development",
  "Motion & graphic design",
  "Publishing",
  "Others",
] as const;
type InterestOption = (typeof INTEREST_OPTIONS)[number];
const INTEREST_PREFILLS: Record<InterestOption, { subject: string; message: string }> = {
  "Art development": {
    subject: "Art Development Inquiry",
    message: "I'd love to learn more about EZ Games' art development services for my project.",
  },
  "Game development": {
    subject: "Game Development Inquiry",
    message: "I'd love to discuss a game development project with EZ Games.",
  },
  "Motion & graphic design": {
    subject: "Motion & Graphic Design Inquiry",
    message: "I'd love to discuss motion and graphic design support for my project with EZ Games.",
  },
  Publishing: {
    subject: "Publishing Inquiry",
    message: "I'd love to discuss publishing opportunities with EZ Games.",
  },
  Others: {
    subject: "General Inquiry",
    message: "I'd love to discuss a potential project or partnership with EZ Games.",
  },
};
const COMPANY_SIZE_OPTIONS = ["1–10", "11–50", "51–200", "201–500", "501+"] as const;

type CustomSelectProps = {
  id: string;
  value: string;
  options: readonly string[];
  placeholder: string;
  controlClassName: string;
  invalid?: boolean;
  required?: boolean;
  formatOption?: (option: string) => string;
  onValueChange: (value: string) => void;
};

function CustomSelect({
  id,
  value,
  options,
  placeholder,
  controlClassName,
  invalid = false,
  required = false,
  formatOption = (option) => option,
  onValueChange,
}: CustomSelectProps) {
  const { t, language } = useI18n("ContactForm");
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(() => Math.max(0, options.indexOf(value)));
  const [openUpward, setOpenUpward] = useState(false);

  useEffect(() => {
    if (!open) return;

    const closeOnOutsidePress = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsidePress);
    return () => document.removeEventListener("pointerdown", closeOnOutsidePress);
  }, [open]);

  const openMenu = () => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (rect) {
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpenUpward(spaceBelow < 240 && rect.top > spaceBelow);
    }
    setHighlightedIndex(Math.max(0, options.indexOf(value)));
    setOpen(true);
  };

  const selectOption = (option: string) => {
    onValueChange(option);
    setOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        openMenu();
        return;
      }
      const step = event.key === "ArrowDown" ? 1 : -1;
      setHighlightedIndex((current) => (current + step + options.length) % options.length);
      return;
    }
    if (event.key === "Home" && open) {
      event.preventDefault();
      setHighlightedIndex(0);
      return;
    }
    if (event.key === "End" && open) {
      event.preventDefault();
      setHighlightedIndex(options.length - 1);
      return;
    }
    if ((event.key === "Enter" || event.key === " ") && open) {
      event.preventDefault();
      selectOption(options[highlightedIndex]);
      return;
    }
    if (event.key === "Escape" && open) {
      event.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        id={id}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${id}-listbox`}
        aria-activedescendant={open ? `${id}-option-${highlightedIndex}` : undefined}
        aria-invalid={invalid}
        aria-required={required}
        className={`${controlClassName} flex items-center justify-between gap-3 text-left ${value ? "text-white" : "text-white/45"} ${open ? "border-brand shadow-[0_0_0_3px_rgba(66,255,117,0.1)]" : ""}`}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={handleKeyDown}
      >
        <span className="min-w-0 truncate">{t(value ? formatOption(value) : placeholder)}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-gold transition-transform duration-200 ${open ? "rotate-180 text-brand" : ""}`}
          strokeWidth={2.2}
          aria-hidden
        />
      </button>

      {open ? (
        <div
          id={`${id}-listbox`}
          role="listbox"
          aria-label={t(placeholder)}
          data-lenis-prevent
          data-lenis-prevent-wheel
          data-lenis-prevent-touch
          className={`absolute z-40 max-h-60 w-full overflow-y-auto overscroll-contain rounded-xl border border-gold/35 bg-[#080d09] p-1.5 shadow-[0_18px_48px_rgba(0,0,0,0.62)] ${openUpward ? "bottom-[calc(100%+0.5rem)]" : "top-[calc(100%+0.5rem)]"}`}
        >
          {options.map((option, index) => {
            const selected = option === value;
            const highlighted = index === highlightedIndex;
            return (
              <button
                id={`${id}-option-${index}`}
                key={option}
                type="button"
                role="option"
                aria-selected={selected}
                className={`flex min-h-10 w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left text-sm font-semibold transition-colors ${
                  selected
                    ? "bg-brand/15 text-brand"
                    : highlighted
                      ? "bg-gold/10 text-gold"
                      : "text-white/75 hover:bg-white/[0.06] hover:text-white"
                }`}
                onMouseEnter={() => setHighlightedIndex(index)}
                onClick={() => selectOption(option)}
              >
                <span>{t(formatOption(option))}</span>
                <Check className={`h-4 w-4 shrink-0 ${selected ? "opacity-100" : "opacity-0"}`} strokeWidth={2.4} aria-hidden />
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

type Props = {
  className?: string;
  motionDelay?: number;
  /** Tighter spacing for chat-style popup */
  compact?: boolean;
  /** Embedded chat popup: single column, full-width submit, light text on dark */
  embeddedPopup?: boolean;
  submitVariant?: PillButtonVariant;
  /** Prefill inquiry fields without clearing contact details already entered. */
  inquiryPrefill?: {
    subject: string;
    message: string;
  } | null;
};

export default function ContactForm({
  className = "",
  motionDelay = 0.2,
  compact = false,
  embeddedPopup = false,
  submitVariant,
  inquiryPrefill = null,
}: Props) {
  const { t, language } = useI18n("ContactForm");
  const { contactDraft, setContactDraft, resetContactDraft } = useContactChat();

  const [localFormData, setLocalFormData] = useState<ContactFormDraft>(() => ({
    ...EMPTY_CONTACT_DRAFT,
  }));

  /** Popup: draft toàn app (giữ khi đổi route / đóng mở). Trang Contact: state riêng. */
  const formData = embeddedPopup ? contactDraft : localFormData;
  const setFormData = embeddedPopup ? setContactDraft : setLocalFormData;

  useEffect(() => {
    if (!inquiryPrefill || embeddedPopup) return;

    setLocalFormData((previous) => ({
      ...previous,
      subject: t(inquiryPrefill.subject),
      message: t(inquiryPrefill.message),
    }));
  }, [embeddedPopup, inquiryPrefill]);

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [website, setWebsite] = useState("");
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const formStartedAt = useRef(Date.now());
  useEffect(() => {
    setCaptchaToken(null);
  }, [language]);

  const emailJsServiceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
  const emailJsTemplateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;
  const emailJsPublicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;
  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.companyName.trim()) {
      newErrors.companyName = "Company name is required";
    }

    if (!formData.companyWebsite.trim()) {
      newErrors.companyWebsite = "Company website is required";
    } else {
      try {
        const websiteUrl = new URL(
          /^https?:\/\//i.test(formData.companyWebsite.trim())
            ? formData.companyWebsite.trim()
            : `https://${formData.companyWebsite.trim()}`,
        );
        if (!websiteUrl.hostname.includes(".")) throw new Error("Invalid hostname");
      } catch {
        newErrors.companyWebsite = "Please enter a valid website";
      }
    }

    if (!formData.companySize) {
      newErrors.companySize = "Please select a company size";
    }

    if (!formData.interest) {
      newErrors.interest = "Please select an area of interest";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    if (!captchaToken) {
      newErrors.captcha = "Please confirm that you are not a robot";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus("idle");

    // Honeypot fields are invisible to people but commonly filled by bots.
    if (website) {
      setSubmitStatus("success");
      return;
    }

    if (Date.now() - formStartedAt.current < MINIMUM_FORM_FILL_TIME_MS) {
      setSubmitStatus("error");
      return;
    }

    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const subject = formData.subject.trim() || `${formData.interest} inquiry`;

      if (!emailJsServiceId || !emailJsTemplateId || !emailJsPublicKey || !recaptchaSiteKey) {
        throw new Error("EmailJS or reCAPTCHA environment variables are not configured");
      }

      await emailjs.send(
        emailJsServiceId,
        emailJsTemplateId,
        {
          // Variables used by EmailJS's Auto-Reply contact template.
          name: formData.name.trim(),
          email: formData.email.trim(),
          title: subject,

          // Detailed aliases retained for custom/internal notification templates.
          from_name: formData.name.trim(),
          from_email: formData.email.trim(),
          reply_to: formData.email.trim(),
          phone: formData.phone.trim() || "-",
          company_name: formData.companyName.trim() || "-",
          company_website: formData.companyWebsite.trim() || "-",
          company_size: formData.companySize || "-",
          job_title: formData.jobTitle.trim() || "-",
          interest: formData.interest,
          subject,
          message: formData.message.trim(),
          to_email: PUBLISHING_EMAIL,
          cc_email: CONTACT_CC_EMAIL,
          "g-recaptcha-response": captchaToken,
        },
        {
          publicKey: emailJsPublicKey,
          blockHeadless: true,
          limitRate: {
            id: "contact-form",
            throttle: 60_000,
          },
        },
      );

      setSubmitStatus("success");
      setCaptchaToken(null);
      recaptchaRef.current?.reset();
      formStartedAt.current = Date.now();
      if (embeddedPopup) {
        resetContactDraft();
      } else {
        setLocalFormData({ ...EMPTY_CONTACT_DRAFT });
      }
    } catch (error) {
      if (error instanceof EmailJSResponseStatus) {
        console.error("EmailJS send failed", { status: error.status, text: error.text });
      } else {
        console.error("Contact form send failed", error);
      }
      setCaptchaToken(null);
      recaptchaRef.current?.reset();
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const gap = embeddedPopup ? "gap-x-4 gap-y-1.5 [@media_(max-height:700px)]:gap-y-1 md:gap-y-2.5 2xl:gap-x-5 2xl:gap-y-4" : compact ? "gap-x-4 gap-y-3" : "gap-x-8 gap-y-5";
  const field = embeddedPopup ? "relative space-y-1 pb-2 [@media_(max-height:700px)]:pb-1.5 md:space-y-1 md:pb-2.5 2xl:space-y-1.5 2xl:pb-3.5" : compact ? "relative space-y-1 pb-3" : "relative space-y-1.5 pb-3";
  const label = compact
    ? `${embeddedPopup ? "text-xs md:text-xs 2xl:text-lg" : "text-[10px] md:text-base"} whitespace-nowrap font-bold uppercase tracking-[0.12em] md:tracking-[0.14em] 2xl:tracking-[0.16em] ${embeddedPopup ? "text-gold/80" : "text-white/45"}`
    : "whitespace-nowrap text-xs font-bold uppercase tracking-widest text-white/40 lg:text-sm";
  const inputPad = embeddedPopup ? "py-1 text-sm leading-tight [@media_(max-height:700px)]:py-0.5 md:py-1.5 md:text-sm md:leading-tight 2xl:py-2.5 2xl:text-lg 2xl:leading-normal" : compact ? "py-2 text-sm" : "py-3 text-base lg:text-lg";
  const taRows = embeddedPopup ? 3 : compact ? 3 : 4;

  const gridCols = embeddedPopup ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-2";
  /** Explicit light text on dark (avoids browser default dark text + bad autofill contrast). */
  const onDark =
    "text-white caret-brand placeholder:text-white/45 autofill:shadow-[inset_0_0_0_1000px_rgb(17_17_17)] autofill:text-white autofill:[-webkit-text-fill-color:white]";
  const fieldBorder = embeddedPopup ? "border-gold/20 focus:border-brand" : "border-white/10 focus:border-brand";
  const inputTheme = embeddedPopup
    ? "bg-black/15 focus:bg-brand/[0.03]"
    : "bg-white/[0.025] focus:bg-white/[0.045]";
  const fullSpan = "md:col-span-2";
  const control = `w-full min-w-0 rounded-xl border px-4 outline-none transition-[border-color,background-color,box-shadow] focus:border-brand focus:shadow-[0_0_0_3px_rgba(66,255,117,0.1)] ${inputPad} ${onDark} ${inputTheme}`;
  const section = `${fullSpan} grid min-w-0 grid-cols-1 gap-x-4 gap-y-1.5 border-t border-white/10 pt-3 md:grid-cols-2 ${embeddedPopup ? "md:pt-3" : "md:gap-x-8 md:gap-y-3 md:pt-5"}`;
  const sectionTitle = `mb-1 font-display text-sm font-bold uppercase tracking-[0.12em] text-gold md:col-span-2 ${embeddedPopup ? "2xl:text-base" : "lg:text-base"}`;
  const submitButtonVariant = submitVariant ?? (embeddedPopup ? "gold" : "accent");
  const noGoldHover =
    "hover:!border-brand/75 hover:!bg-brand/10 hover:!shadow-[0_0_28px_rgba(66,255,117,0.18)] group-hover:[&_*]:!text-gold";
  const errorText = embeddedPopup
    ? "absolute bottom-0 left-0 min-h-2.5 text-[9px] font-semibold leading-none text-red-400 [text-wrap:pretty] md:min-h-2.5 md:text-[9px] 2xl:min-h-3 2xl:text-[11px]"
    : "absolute bottom-0 left-0 min-h-3 text-[11px] font-semibold leading-none text-red-400 [text-wrap:pretty]";

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, x: 20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay: motionDelay }}
      className={`contact-form grid ${gridCols} ${gap} ${embeddedPopup ? "content-start pb-0 md:pb-0" : ""} ${className}`}
    >
      <div className="pointer-events-none absolute -left-[10000px] h-px w-px overflow-hidden" aria-hidden="true">
        <label htmlFor={embeddedPopup ? "popup-website" : "contact-website"}>{t("Website")}</label>
        <input
          id={embeddedPopup ? "popup-website" : "contact-website"}
          type="text"
          name="website"
          value={website}
          onChange={(event) => setWebsite(event.target.value)}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <fieldset className={section}>
        <legend className={sectionTitle}>{t("Your information")}</legend>
      <div className={`min-w-0 ${field}`}>
        <label className={label} htmlFor={embeddedPopup ? "popup-name" : "contact-name"}>{t("Full name")}</label>
        <input
          id={embeddedPopup ? "popup-name" : "contact-name"}
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          maxLength={100}
          className={`${control} ${errors.name ? "border-red-500" : fieldBorder}`}
          autoComplete="name"
          required
          aria-invalid={Boolean(errors.name)}
          placeholder={t("Your full name")}
        />
        <p className={errorText}>{t(errors.name ?? "")}</p>
      </div>
      <div className={`min-w-0 ${field}`}>
        <label className={label} htmlFor={embeddedPopup ? "popup-email" : "contact-email"}>{t("Email")}</label>
        <input
          id={embeddedPopup ? "popup-email" : "contact-email"}
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          maxLength={254}
          className={`${control} ${errors.email ? "border-red-500" : fieldBorder}`}
          autoComplete="email"
          required
          aria-invalid={Boolean(errors.email)}
          placeholder={t("hello@example.com")}
        />
        <p className={errorText}>{t(errors.email ?? "")}</p>
      </div>

      <div className={`min-w-0 ${field}`}>
        <label className={label} htmlFor={embeddedPopup ? "popup-phone" : "contact-phone"}>{t("Phone number")}</label>
        <input
          id={embeddedPopup ? "popup-phone" : "contact-phone"}
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          maxLength={30}
          className={`${control} ${fieldBorder}`}
          autoComplete="tel"
          placeholder={t("Your phone number")}
        />
      </div>

      <div className={`min-w-0 ${field}`}>
        <label className={label} htmlFor={embeddedPopup ? "popup-job-title" : "contact-job-title"}>{t("Job title")}</label>
        <input
          id={embeddedPopup ? "popup-job-title" : "contact-job-title"}
          type="text"
          name="jobTitle"
          value={formData.jobTitle}
          onChange={handleChange}
          maxLength={120}
          className={`${control} ${fieldBorder}`}
          autoComplete="organization-title"
          placeholder={t("e.g. Producer, Art Director")}
        />
      </div>
      </fieldset>

      <fieldset className={section}>
        <legend className={sectionTitle}>{t("Company information")}</legend>
      <div className={`min-w-0 ${field}`}>
        <label className={label} htmlFor={embeddedPopup ? "popup-company" : "contact-company"}>{t("Company name")}</label>
        <input
          id={embeddedPopup ? "popup-company" : "contact-company"}
          type="text"
          name="companyName"
          value={formData.companyName}
          onChange={handleChange}
          maxLength={160}
          className={`${control} ${errors.companyName ? "border-red-500" : fieldBorder}`}
          autoComplete="organization"
          required
          aria-invalid={Boolean(errors.companyName)}
          placeholder={t("Company or studio")}
        />
        <p className={errorText}>{t(errors.companyName ?? "")}</p>
      </div>

      <div className={`min-w-0 ${field}`}>
        <label className={label} htmlFor={embeddedPopup ? "popup-company-website" : "contact-company-website"}>{t("Company website")}</label>
        <input
          id={embeddedPopup ? "popup-company-website" : "contact-company-website"}
          type="text"
          inputMode="url"
          name="companyWebsite"
          value={formData.companyWebsite}
          onChange={handleChange}
          maxLength={2048}
          className={`${control} ${errors.companyWebsite ? "border-red-500" : fieldBorder}`}
          autoComplete="url"
          required
          aria-invalid={Boolean(errors.companyWebsite)}
          placeholder={t("company.com")}
        />
        <p className={errorText}>{t(errors.companyWebsite ?? "")}</p>
      </div>

      <div className={`min-w-0 ${fullSpan} ${field}`}>
        <label className={label} htmlFor={embeddedPopup ? "popup-company-size" : "contact-company-size"}>{t("Company size")}</label>
        <CustomSelect
          id={embeddedPopup ? "popup-company-size" : "contact-company-size"}
          value={formData.companySize}
          options={COMPANY_SIZE_OPTIONS}
          placeholder={t("Select company size")}
          controlClassName={`${control} ${errors.companySize ? "border-red-500" : fieldBorder}`}
          required
          invalid={Boolean(errors.companySize)}
          formatOption={(option) => t("{{count}} people", { count: option })}
          onValueChange={(companySize) => {
            setFormData((previous) => ({ ...previous, companySize }));
            setErrors((previous) => ({ ...previous, companySize: undefined }));
          }}
        />
        <p className={errorText}>{t(errors.companySize ?? "")}</p>
      </div>
      </fieldset>

      <fieldset className={section}>
        <legend className={sectionTitle}>{t("Project information")}</legend>
      <div className={`min-w-0 ${fullSpan} ${field}`}>
        <label className={label} htmlFor={embeddedPopup ? "popup-interest" : "contact-interest"}>{t("You are interested in")}</label>
        <CustomSelect
          id={embeddedPopup ? "popup-interest" : "contact-interest"}
          value={formData.interest}
          options={INTEREST_OPTIONS}
          placeholder={t("Select a service")}
          controlClassName={`${control} ${errors.interest ? "border-red-500" : fieldBorder}`}
          required
          invalid={Boolean(errors.interest)}
          onValueChange={(interest) => {
            const prefill = INTEREST_PREFILLS[interest as InterestOption];
            setFormData((previous) => ({
              ...previous,
              interest,
              subject: t(prefill.subject),
              message: t(prefill.message),
            }));
            setErrors((previous) => ({ ...previous, interest: undefined, message: undefined }));
          }}
        />
        <p className={errorText}>{t(errors.interest ?? "")}</p>
      </div>

      <div className={`min-w-0 ${fullSpan} ${field}`}>
        <label className={label} htmlFor={embeddedPopup ? "popup-message" : "contact-message"}>{t("Your message")}</label>
        <textarea
          id={embeddedPopup ? "popup-message" : "contact-message"}
          name="message"
          value={formData.message}
          onChange={handleChange}
          maxLength={5000}
          required
          aria-invalid={Boolean(errors.message)}
          rows={taRows}
          className={`${control} resize-none ${errors.message ? "border-red-500" : fieldBorder}`}
          placeholder={t("Tell us about your project")}
        />
        <p className={errorText}>{t(errors.message ?? "")}</p>
      </div>
      </fieldset>

      <div className={`min-w-0 ${fullSpan}`}>
        {recaptchaSiteKey ? (
          <div className="max-w-full overflow-hidden">
            <ReCAPTCHA
              key={language}
              hl={language === "zh" ? "zh-CN" : language}
              ref={recaptchaRef}
              sitekey={recaptchaSiteKey}
              theme="dark"
              onChange={(token) => {
                setCaptchaToken(token);
                setErrors((previous) => ({ ...previous, captcha: undefined }));
              }}
              onExpired={() => {
                setCaptchaToken(null);
                setErrors((previous) => ({ ...previous, captcha: "Verification expired. Please try again" }));
              }}
              onErrored={() => {
                setCaptchaToken(null);
                setErrors((previous) => ({ ...previous, captcha: "Verification failed. Please try again" }));
              }}
            />
          </div>
        ) : (
          <p className="text-sm font-semibold text-red-400">{t("Verification is temporarily unavailable.")}</p>
        )}
        <p className="mt-1 min-h-4 text-[11px] font-semibold text-red-400 [text-wrap:pretty]">{t(errors.captcha ?? "")}</p>
      </div>

      {embeddedPopup && submitStatus === "idle" ? null : (
        <p
          className={`${embeddedPopup ? "min-h-3 text-[10px] md:min-h-3 md:text-[10px] 2xl:min-h-5 2xl:text-sm" : "min-h-5 text-sm"} font-bold leading-snug ${
            submitStatus === "success" ? "text-green-500" : submitStatus === "error" ? "text-red-500" : "text-transparent"
          } [text-wrap:pretty] ${fullSpan}`}
        >
          {t(submitStatus === "success"
            ? "Message sent successfully."
            : submitStatus === "error"
              ? "Something went wrong. Please try again."
              : ".")}
        </p>
      )}

      <div
        className={
          embeddedPopup
            ? `w-full ${fullSpan} md:w-full`
            : `flex justify-end md:col-span-2`
        }
      >
        <PillButton
          type="submit"
          disabled={isSubmitting || !recaptchaSiteKey}
          variant={submitButtonVariant}
          className={
            embeddedPopup
              ? `!flex !min-h-9 w-auto max-w-[min(15rem,78vw)] justify-center border-gold/70 bg-gold/10 px-5 py-1.5 text-[11px] tracking-[0.18em] shadow-[0_0_24px_rgba(241,200,106,0.18)] [@media_(max-height:700px)]:py-1 md:!min-h-9 md:w-full md:max-w-full md:px-5 md:py-1.5 md:text-xs md:tracking-[0.17em] 2xl:!min-h-10 2xl:px-7 2xl:py-2.5 2xl:text-[15px] 2xl:tracking-[0.2em] ${noGoldHover}`
              : compact
                ? `px-6 py-2 text-[12px] shadow-lg shadow-orange/20 ${submitButtonVariant === "gold" ? noGoldHover : ""}`
              : `px-8 py-2.5 text-[12px] shadow-xl shadow-orange/20 lg:text-sm ${submitButtonVariant === "gold" ? noGoldHover : ""}`
          }
          label={t(isSubmitting ? "SENDING..." : "SEND MESSAGE")}
          icon={
            isSubmitting ? undefined : <SectionArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          }
        />
      </div>
    </motion.form>
  );
}
